/// <reference types="@types/segment-analytics" />

import Vue from 'vue';
import App from './App.vue';
import VueI18n from 'vue-i18n';
import { getRouter } from './router';
import store from './store';
import messages from '@/i18n';
import VueTheMask from 'vue-the-mask';
import VueMask from 'v-mask';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import * as VeeValidate from 'vee-validate';
import { Validator } from 'vee-validate';

import vuescroll from 'vue-scroll';
import VueApexCharts from 'vue-apexcharts';
import VueTippy, { TippyComponent } from 'vue-tippy';
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css';
import 'tippy.js/themes/light.css';
import 'tippy.js/themes/light-border.css';
import 'tippy.js/dist/border.css';
import 'vue-advanced-cropper/dist/style.css';
import 'vue2-timepicker/dist/VueTimepicker.css';
import 'vue-draggable-resizable/dist/VueDraggableResizable.css';
import smoothscroll from 'smoothscroll-polyfill';
import VueAnalytics from 'vue-analytics';
import VueGtag from 'vue-gtag';
import { VueReCaptcha } from 'vue-recaptcha-v3';
import VueTour from 'vue-tour';

require('vue-tour/dist/vue-tour.css');

// TODO: add iframe parent url validation

Vue.use(VueTour as any);

import { ConfirmDialogOptions } from '@/models/confirm-dialog';
import { AnalyticsOptions, AnalyticsSendTo } from '@/models/analytics-options';

import FlagService from '@/api/FlagService';

declare global {
  interface Window {
    app: Vue;
    hj: (identifierAction: 'event', eventName: string) => void;
    analytics: SegmentAnalytics.AnalyticsJS;
    mixpanel: any;
  }
}

Vue.use(VueAnalytics, {
  id: process.env.VUE_APP_GA_ID
});

Vue.use(VueGtag, {
  config: {
    id: process.env.VUE_APP_GTAG_ID
  }
});

Vue.use(VueReCaptcha, {
  siteKey: process.env.VUE_APP_RECAPTCHA_SITE_KEY,
  loaderOptions: {
    useRecaptchaNet: true
  }
});

/** SwiperJs Modules */
import {
  Swiper as SwiperClass,
  Pagination,
  Navigation,
  Autoplay,
  EffectFade
} from 'swiper/core';
import 'swiper/swiper-bundle.css';
import 'swiper/swiper.min.css';
import 'swiper/components/navigation/navigation.min.css';
import 'swiper/components/pagination/pagination.min.css';
import getAwesomeSwiper from 'vue-awesome-swiper/dist/exporter';

SwiperClass.use([Navigation, Pagination, Autoplay, EffectFade]);

Vue.use(getAwesomeSwiper(SwiperClass));
/********************/

smoothscroll.polyfill();

Vue.use(VueTippy, {
  zIndex: 9999999
});
Vue.component('VueCtkDateTimePicker', VueCtkDateTimePicker);
Vue.component('tippy', TippyComponent);
Vue.component('ApexChart', VueApexCharts);
Vue.use(vuescroll);
import {
  checkEqual,
  password,
  fullName,
  simplePhone,
  EIN,
  noRequired
} from '@/utils/custom-rules';
import Notifications from 'vue-notification';
import APIService from '@/api';
import AirbnbStyleDatepicker from 'dp-vue-airbnb-style-datepicker';
import VueProgressiveImage from 'vue-progressive-image';
import { ELanguages } from './models/internationalization';
import { INotificationData } from './models/notification';
import './vee-validate';
import { InitialAdminPath } from './constants/routes';
import logger, { initDatadogLibrary, initWebVitals } from './utils/logger';

const vClickOutside = require('v-click-outside');

APIService.readCredentials(window.location.pathname.includes('/admin'));

Vue.config.productionTip = false;

initDatadogLibrary();
initWebVitals();

dayjs.extend(utc);

Vue.directive('click-outside', {
  bind: function(el: any, binding: any, vnode: any) {
    el.clickOutsideEvent = function(event: any) {
      // check that click was outside the el and his childrens
      if (!(el == event.target || el.contains(event.target))) {
        // if it did, call method provided in attribute value
        vnode.context[binding.expression](event);
      }
    };
    document.body.addEventListener('click', el.clickOutsideEvent);
  },
  unbind: function(el: any) {
    document.body.removeEventListener('click', el.clickOutsideEvent);
  }
});

Vue.use(VueProgressiveImage, {
  blur: 30,
  cache: true,
  delay: 700
});
Vue.use(VueI18n);
Vue.use(VueTheMask);
Vue.use(VueMask);
Vue.use(VeeValidate, { inject: false });
Vue.use(Notifications);
Vue.use(vClickOutside);
Vue.directive('v-click-outside', vClickOutside.directive);

const language = ELanguages.EN_US;

Validator.localize(language);
Validator.localize(messages);

Validator.extend('EIN', EIN);
Validator.extend('checkEqual', checkEqual);
Validator.extend('password', password);
Validator.extend('simplePhone', simplePhone);
Validator.extend('noRequired', noRequired);
Validator.extend('fullName', fullName);

export const i18n = new VueI18n({
  locale: language,
  fallbackLocale: {
    pt: [ELanguages.PT_PT],
    default: [ELanguages.EN_US]
  },
  messages
});

// declares augmentation for Vue
declare module 'vue/types/vue' {
  interface Vue {
    $showLoader: (withoutTimer?: boolean) => void;
    $modal: any;
    $showLoaderWithLocker: (withoutTimer?: boolean) => void;
    $hideLoader: () => void;
    $notification: (
      text: string,
      group?: string,
      duration?: number,
      options?: INotificationData
    ) => void;
    $mobileCheck: () => boolean;
    $analytics: (
      data: AnalyticsOptions,
      sendTo?: Array<AnalyticsSendTo>
    ) => void;
    $lock: () => void;
    $unlock: () => void;
    $showConfirmDialog: (
      options?: Partial<ConfirmDialogOptions>
    ) => Promise<any>;
    $showMediumBackgroundLocker: () => void;
    $hideMediumBackgroundLocker: () => void;
    $cdn: (relativeAssetPath: string) => string;
    VMask: (format: string) => any;
  }

  interface VueConstructor {
    showLoader: () => void;
    showLoaderWithLocker: () => void;
    hideLoader: () => void;
    analytics: (
      data: AnalyticsOptions,
      sendTo?: Array<AnalyticsSendTo>
    ) => void;
    notification: (
      text: string,
      group?: string,
      duration?: number,
      options?: INotificationData
    ) => void;
    lock: () => void;
    unlock: () => void;
    showConfirmDialog: (
      options?: Partial<ConfirmDialogOptions>
    ) => Promise<any>;
    showMediumBackgroundLocker: () => void;
    hideMediumBackgroundLocker: () => void;
  }
}

const flagService = new FlagService();

const getFlags = async () => {
  let items = "['']";

  try {
    const flagResponse = await flagService.getFlags();
    items = JSON.stringify(flagResponse.data.items);

    return {
      success: true,
      data: flagResponse.data.items
    };
  } catch {
    return {
      success: false,
      data: []
    };
  } finally {
    sessionStorage.setItem('dashboard-flags', items);
  }
};

let app: Vue;

const mountApplication = async () => {
  const { data: flags } = await getFlags();

  const router = getRouter();

  app = new Vue({
    router,
    store,
    i18n,
    render: h => h(App, { props: { featureFlags: flags || [] } })
  }).$mount('#app');

  console.debug('[Legacy dashboard] export main app');
  window.app = app;
};

const redirectRouteIfNeeds = () => {
  if (APIService.token && document.location.pathname === '/') {
    window.location.assign(InitialAdminPath());

    return;
  }
};

const bootstrap = async () => {
  redirectRouteIfNeeds();
  await mountApplication();

  const datepickerOptions = {
    sundayFirst: true,
    days: app.$t('datepicker.days'),
    daysShort: app.$t('datepicker.daysShort'),
    monthNames: app.$t('datepicker.monthNames'),
    colors: {
      disabled: '#AFB0B3',
      selected: '#D51648'
    },
    enableKeyboardShortcuts: false
  };
  Vue.use(AirbnbStyleDatepicker, datepickerOptions);
};

bootstrap();

export { app };
